/**
 * Spracherkennung der I18n_Infrastruktur.
 *
 * Bestimmt die initiale Aktive_Sprache aus der Browser-Spracheinstellung
 * (`navigator.languages` / `navigator.language`). Die Funktion ist rein und
 * nimmt die zu prüfenden Sprach-Tags optional als Argument entgegen, damit sie
 * property-testbar bleibt; ohne Argument greift sie defensiv auf `navigator` zu.
 */

import { type Language, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "./catalog";

/**
 * Liest die Browser-Sprach-Tags defensiv aus `navigator` aus.
 *
 * `navigator` kann in Testumgebungen (oder beim SSR) fehlen; in diesem Fall
 * wird eine leere Liste zurückgegeben, sodass `detectLanguage` auf die
 * Standardsprache zurückfällt.
 */
function readNavigatorLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return [];
  if (navigator.languages && navigator.languages.length > 0) {
    return navigator.languages;
  }
  return navigator.language ? [navigator.language] : [];
}

/**
 * Bestimmt die initiale Aktive_Sprache aus den Browser-Sprach-Tags.
 *
 * Prüft die übergebenen (oder die aus `navigator` gelesenen) Sprach-Tags per
 * Präfix-Vergleich auf eine Unterstützte_Sprache (z. B. "en-US" → "en",
 * "de-DE" → "de"). Der erste unterstützte Treffer gewinnt (Req 8.1, 8.2);
 * findet sich keiner, wird die Standardsprache (`de`) zurückgegeben (Req 8.3).
 * Das Ergebnis ist immer eine Unterstützte_Sprache.
 */
export function detectLanguage(navLanguages?: readonly string[]): Language {
  const tags = navLanguages ?? readNavigatorLanguages();

  for (const tag of tags) {
    if (typeof tag !== "string") continue;
    const prefix = tag.toLowerCase().split("-")[0];
    const match = SUPPORTED_LANGUAGES.find((lang) => lang === prefix);
    if (match) return match; // erster unterstützter Treffer gewinnt (Req 8.1, 8.2)
  }

  return DEFAULT_LANGUAGE; // kein unterstützter Treffer (Req 8.3)
}
